import { useState } from "react";
import PropTypes from "prop-types";
import ScheduleCard from "@/pages/event_management/single_campaign/components/campaign_tabs/Schedule_Card";

const CalendarTab = ({ schedules }) => {
  const [selectedDay, setSelectedDay] = useState("Mon"); // Default selected day

  // Shortened day names for mobile
  const daysOfWeek = [
    { short: "Sun", full: "Sunday" },
    { short: "Mon", full: "Monday" },
    { short: "Tue", full: "Tuesday" },
    { short: "Wed", full: "Wednesday" },
    { short: "Thu", full: "Thursday" },
    { short: "Fri", full: "Friday" },
    { short: "Sat", full: "Saturday" },
  ];

  const timeSlots = Array.from({ length: 24 }, (_, i) => {
    const hour = i % 12 || 12;
    const ampm = i < 12 ? "AM" : "PM";
    return `${hour}:00 ${ampm}`;
  });

  const getHourFromTimeString = (timeString) => {
    const [time, period] = timeString.split(" ");
    let [hours] = time.split(":").map(Number);
    if (period === "PM" && hours !== 12) hours += 12;
    if (period === "AM" && hours === 12) hours = 0;
    return hours;
  };

  // Calculate schedule position and duration
  const getSchedulePosition = (schedule) => {
    const [startTime, endTime] = schedule.timeRange.split(" - ");
    const startHour = getHourFromTimeString(startTime);
    const endHour = getHourFromTimeString(endTime);

    // Mock day assignment - in real app, you'd use actual date
    const dayIndex = 1; // Monday for example

    return {
      gridRowStart: startHour + 1,
      gridRowEnd: endHour + 1,
      gridColumn: dayIndex + 1, // +1 because first column is time labels
      height: `${(endHour - startHour) * 100}%`,
    };
  };

  return (
    <div className="calendar-view border rounded-lg overflow-auto">
      {/* Days Header */}
      <div className="grid grid-cols-8 border-b sticky top-0 bg-white z-10">
        <div className="p-2 border-r bg-gray-50"></div>
        <div className="col-span-7 flex overflow-x-auto hide-scrollbar">
          {daysOfWeek.map((day) => (
            <button
              key={day.short}
              onClick={() => setSelectedDay(day.short)}
              className={`
                flex-1 min-w-[80px] p-2 m-1 rounded-md
                text-sm sm:text-base font-medium
                transition-all duration-200
                ${
                  selectedDay === day.short
                    ? "bg-primary text-white"
                    : "text-gray-700 hover:bg-primary/10"
                }
              `}>
              <span className="block sm:hidden">{day.short}</span>
              <span className="hidden sm:block">{day.full}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="relative grid grid-cols-8">
        {/* Time Labels */}
        <div className="col-span-1 border-r sticky left-0 bg-white z-10 w-[60px] sm:w-auto">
          {timeSlots.map((time) => (
            <div
              key={time}
              className="h-20 border-b p-1 sm:p-2 text-xs sm:text-sm text-gray-600">
              {time}
            </div>
          ))}
        </div>

        {/* Grid Background */}
        <div className="col-span-7 grid grid-cols-7 relative">
          {/* Grid lines */}
          {Array.from({ length: 7 * 24 }).map((_, i) => (
            <div key={i} className="h-20 border-b border-r" />
          ))}

          {/* Schedule Cards - Using the ScheduleCard component */}
          {schedules.map((schedule) => {
            const position = getSchedulePosition(schedule);
            return (
              <div
                key={schedule.id}
                className="absolute"
                style={{
                  ...position,
                  width: "calc(100% - 8px)",
                }}>
                <ScheduleCard schedule={schedule} />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

CalendarTab.propTypes = {
  schedules: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      title: PropTypes.string,
      address: PropTypes.string,
      location: PropTypes.string,
      timeRange: PropTypes.string,
    })
  ),
};

export default CalendarTab;
